import { catchError, map, tap, Observable, of } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { FunctionRegistryService } from '../../../function-registry/function-registry.service';
import { EducationalPathDTO } from '../../../../dto/educational-path/educational-path.dto';
import { EducationalPathItemDTO } from '../../../../dto/educational-path/educational-path-item.dto';
import { EducationalPathVersionMaintenanceDTO } from '../../../../dto/educational-path/educational-path-version-maintenance.dto';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';

interface PathItemResponse {
  id: number;
  type: string;
  name: string;
  created_at?: string | null;
  updated_at?: string | null;
  item_details?: any;
  version_details?: any;
  module_version_id?: number;
  subtype_version_id?: number;
  maintenance_minutes?: number;
  required?: boolean;
  module_id?: number;
  subtype_id?: number;
  version_id?: number;
  start_date?: string;
  end_date?: string;
}

const translateType = (type: string): string => {
  switch (type.toLowerCase()) {
    case 'modules': return 'Modul';
    case 'subtype': return 'Indholdstema';
    case 'goal': return 'Kompetencemål';
    default: return type;
  }
};

export function educationalPathMaintenance(componentDataSourceService: ComponentDataSourceService, functionRegistryService: FunctionRegistryService) {
  return {
    name: 'Educational Path Maintenance',
    description: 'Lists all maintenance items for a specific educational path version',
    execute: (params?: LoadDataParams) => {
      
      // Get the version ID from the route
      const versionId = componentDataSourceService.getCurrentVersionId();
      if (!versionId) {
        console.warn('No version ID found in route');
        return of([]);
      }

      // Use the standardized API approach like educational-paths.ts
      return componentDataSourceService['loadDataWithStandardFiltersGET'](
        'educational_path_maintenance', 
        `/paths/versions/${versionId}/maintenance`, 
        params
      ).pipe(
        tap(result => {
        }),
        catchError(error => {
          console.error('Error fetching educational path maintenance:', error);
          return of({ items: [], totalCount: 0 });
        }),
        map((result: any) => {
          // Support ListResponseDTO (data + meta) and legacy (items + totalCount)
          let items: PathItemResponse[] = [];
          let totalCount = 0;
          let pageSize = result?.pageSize ?? result?.meta?.pageSize ?? 10;
          let pageIndex = result?.pageIndex ?? (result?.meta?.page != null ? result.meta.page - 1 : 0);

          if (result?.data && result?.meta) {
            items = result.data;
            totalCount = result.meta.totalItems ?? 0;
            pageSize = result.meta.pageSize ?? 10;
            pageIndex = result.meta.page != null ? result.meta.page - 1 : 0;
          } else if (result?.items) {
            items = result.items;
            totalCount = result.totalCount ?? 0;
            pageSize = result.pageSize ?? 10;
            pageIndex = result.pageIndex ?? 0;
          }

          if (!items.length) {
            return { items: [], totalCount: 0, pageSize, pageIndex };
          }

          try {
            const mappedItems = items.map((item: PathItemResponse) => {
              const name = item.item_details?.name || 'Unnamed';
              const itemId = item.module_id || item.subtype_id;
              const type = item.module_id ? 'Module' : 'Subtype';
              const translatedType = translateType(type);

              const mapped = {
                ...item,
                id: item.id,
                type,
                itemType: translatedType,
                name,
                itemName: name,
                itemId,
                item_id: item.id,
                version_id: item.version_id,
                start_date: item.start_date,
                end_date: item.end_date,
                maintenance_minutes: item.item_details?.maintenance_minutes || item.maintenance_minutes || 0,
                required: item.required !== undefined ? item.required : true
              };
              return mapped;
            });

            return {
              items: mappedItems,
              totalCount,
              pageSize,
              pageIndex
            };
          } catch (error) {
            console.error('Error converting educational path maintenance:', error);
            return { items: [], totalCount: 0, pageSize, pageIndex };
          }
        })
      );
    },
    settings: {
      columns: [
        { key: 'itemName', header: 'Name', type: 'text', sortable: true },
        { key: 'itemType', header: 'Type', type: 'text', sortable: true },
        { key: 'start_date', header: 'Start dato', type: 'date', sortable: true },
        { key: 'end_date', header: 'Slut dato', type: 'date', sortable: true },
        { key: 'maintenance_minutes', header: 'Minutter', type: 'number', sortable: true },
        { key: 'required', header: 'Påkrævet', type: 'boolean', sortable: true },
        { 
          key: 'remove',
          header: '',
          type: 'button',
          variant: 'raised',
          buttonText: 'Fjern',
          buttonColor: 'warn',
          action: (row: any) => {
            const versionId = componentDataSourceService.getCurrentVersionId();
            if (versionId && row?.id != null) {
              functionRegistryService.executeFunction('removeMaintenance', [versionId, row.id]);
            }
          }
        }
      ],
      pageable: true,
      sortable: true,
      rowHover: true,
      defaultSortColumn: 'maintenance_minutes',
      defaultSortDirection: 'desc'
    }
  }
} 